import React, { useMemo, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useData } from "../context/DataContext";
import { cellText } from "../utils/helpers";

// Bulk-remove selected employees from the tasks currently on screen.
//
// Semantics agreed with the user:
//   • the selected people are taken off each task
//   • a task that still has someone left keeps running for them
//   • a task left with NOBODY is deleted, because it belongs to no one
// So filtering by one person detaches them; filtering by everyone on a task
// removes the task outright.
//
// Deliberately built on the two existing, proven endpoints (updateTask /
// deleteTask) rather than a new bulk API — assignee removal already cleans up
// user_tasks rows server-side, and no new backend code means no new risk.

// Each update makes the worker kick off a background Lark sync. Firing
// hundreds of these at once overwhelmed it — Cloudflare then returns an error
// page with no CORS headers, which the browser surfaces as "Failed to fetch".
// So: low concurrency, a breath between requests, and retries with backoff.
// One request per CHUNK of tasks, not per task. 660 tasks used to mean 660
// round trips (minutes, and enough background Lark syncs to make the worker
// start returning errors). Now it is ~4 requests, each handled server-side
// with a single D1 batch and Lark's batch_update / batch_delete.
const CHUNK = 50;
const MAX_ATTEMPTS = 3;

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

function assigneesOf(fields) {
  const raw = fields?.["Total Assigned Users"] || fields?.["All Assigned Users"] || fields?.["Assigned Users"] || fields?.["User"];
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

export default function BulkAssigneeRemoveModal({ records, tableId, selectedNames, onClose }) {
  const { bulkRemoveAssignee, refresh } = useData();
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [failed, setFailed] = useState([]);
  const [finished, setFinished] = useState(false);
  const [planned, setPlanned] = useState(0);
  const [succeeded, setSucceeded] = useState(0);
  const cancelRef = useRef(false);

  const targets = useMemo(() => {
    const wanted = new Set((selectedNames || []).map(n => n.trim().toLowerCase()));
    const willUpdate = [], willDelete = [];
    for (const r of (records || [])) {
      const users = assigneesOf(r.fields);
      const keep = users.filter(u => !wanted.has(String(u.en_name || u.name || "").trim().toLowerCase()));
      if (keep.length === users.length) continue; // nobody selected is on this task
      (keep.length === 0 ? willDelete : willUpdate).push({ record: r, keep });
    }
    return { willUpdate, willDelete, total: willUpdate.length + willDelete.length };
  }, [records, selectedNames]);

  const run = async () => {
    setRunning(true); cancelRef.current = false;
    const jobs = [...targets.willUpdate, ...targets.willDelete];
    setPlanned(jobs.length);

    // Names -> the open_ids the server removes. Resolved from the tasks
    // themselves so a rename in Lark can't break the match.
    const wanted = new Set((selectedNames || []).map(n => n.trim().toLowerCase()));
    const removeUserIds = new Set();
    for (const r of (records || [])) {
      for (const u of assigneesOf(r.fields)) {
        const nm = String(u.en_name || u.name || "").trim().toLowerCase();
        const id = u.id || u.open_id;
        if (id && wanted.has(nm)) removeUserIds.add(String(id));
      }
    }

    const chunks = [];
    for (let i = 0; i < jobs.length; i += CHUNK) {
      chunks.push(jobs.slice(i, i + CHUNK).map(j => j.record.record_id));
    }

    const fails = [];
    let ok = 0;
    for (const c of chunks) {
      if (cancelRef.current) break;
      let lastErr = null;
      for (let a = 1; a <= MAX_ATTEMPTS; a++) {
        try {
          const res = await bulkRemoveAssignee(c, Array.from(removeUserIds));
          ok += (res?.updated || 0) + (res?.deleted || 0);
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          if (a < MAX_ATTEMPTS) await sleep(600 * a);
        }
      }
      if (lastErr) fails.push({ label: `${c.length} tasks`, msg: lastErr?.message || "failed" });
      setDone(d => d + c.length);
    }

    setSucceeded(ok);
    setFailed(fails);
    setFinished(true);
    setRunning(false);
    refresh(true);
  };

  const names = (selectedNames || []).join(", ");
  const canRun = confirmText.trim() === String(targets.total) && targets.total > 0;

  const styles = `
    .bulk-sub {
      font-size: 12px; font-weight: 600; color: var(--text-tertiary);
      margin-top: 2px; max-width: 340px;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .bulk-body { font-size: 13px; line-height: 1.5; }
    .bulk-hero {
      display: flex; align-items: baseline; gap: 8px;
      padding-bottom: 14px; margin-bottom: 14px;
      border-bottom: 1px solid var(--border-subtle);
    }
    .bulk-hero-num {
      font-size: 30px; font-weight: 800; line-height: 1;
      color: var(--text-primary); font-variant-numeric: tabular-nums;
    }
    .bulk-hero-label { font-size: 13px; color: var(--text-tertiary); font-weight: 600; }
    .bulk-stats { display: flex; flex-direction: column; gap: 2px; }
    .bulk-stat {
      display: flex; align-items: center; gap: 10px;
      padding: 11px 12px; border-radius: var(--radius);
      background: var(--surface-1);
    }
    .bulk-stat.danger { background: var(--danger-muted); }
    .bulk-stat-dot {
      width: 8px; height: 8px; border-radius: 50%;
      background: var(--border-strong); flex-shrink: 0;
    }
    .bulk-stat-dot.ok  { background: var(--success); }
    .bulk-stat-dot.bad { background: var(--danger); }
    .bulk-stat-label { flex: 1; font-size: 12.5px; font-weight: 600; color: var(--text-secondary); }
    .bulk-stat.danger .bulk-stat-label { color: var(--danger); font-weight: 700; }
    .bulk-stat-num {
      font-size: 15px; font-weight: 800; color: var(--text-primary);
      font-variant-numeric: tabular-nums;
    }
    .bulk-stat.danger .bulk-stat-num { color: var(--danger); }
    .bulk-warn {
      display: flex; gap: 8px; align-items: flex-start;
      margin-top: 14px; padding: 10px 12px;
      border-radius: var(--radius);
      background: var(--danger-muted); color: var(--danger);
      font-size: 12px; font-weight: 600; line-height: 1.45;
    }
    .bulk-confirm-label {
      display: block; font-size: 12px; font-weight: 600;
      color: var(--text-secondary); margin: 18px 0 6px;
    }
    .bulk-confirm-label b { color: var(--text-primary); font-weight: 800; }
    .bulk-confirm-input {
      width: 100%; min-height: 40px; padding: 9px 13px;
      border: 1px solid var(--border-default); border-radius: var(--radius);
      background: var(--surface-1); color: var(--text-primary);
      font-family: inherit; font-size: 14px; font-weight: 700;
      font-variant-numeric: tabular-nums;
      transition: var(--transition-fast);
    }
    .bulk-confirm-input:focus {
      outline: none; border-color: var(--primary);
      box-shadow: 0 0 0 3px var(--primary-light);
    }
    .bulk-progress-track {
      height: 6px; border-radius: 3px; overflow: hidden;
      background: var(--surface-3); margin: 18px 0 8px;
    }
    .bulk-progress-fill {
      height: 100%; background: var(--primary);
      border-radius: 3px; transition: width .25s ease;
    }
    .bulk-progress-text {
      display: flex; justify-content: space-between;
      font-size: 12px; font-weight: 700; color: var(--text-secondary);
      font-variant-numeric: tabular-nums;
    }
    .bulk-hint { font-size: 11.5px; color: var(--text-tertiary); margin-top: 4px; }
    .bulk-done { display: flex; gap: 12px; align-items: center; }
    .bulk-done-icon { font-size: 26px; line-height: 1; }
    .bulk-done-title { font-size: 15px; font-weight: 800; color: var(--text-primary); }
    .bulk-done-sub { font-size: 12.5px; color: var(--text-tertiary); margin-top: 2px; }
    .bulk-fail {
      margin-top: 14px; padding: 10px 12px;
      border-radius: var(--radius);
      background: var(--danger-muted); color: var(--danger);
      font-size: 12px; max-height: 150px; overflow-y: auto;
    }
    .bulk-fail ul { margin: 6px 0 0 16px; padding: 0; }
    @media (max-width: 560px) {
      .bulk-hero-num { font-size: 26px; }
      .modal-footer { flex-direction: column-reverse; }
      .modal-footer .btn { width: 100%; }
    }
  `;

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={() => (!running ? onClose() : null)}>
      <style>{styles}</style>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 style={{ margin: 0 }}>Remove from tasks</h3>
            <div className="bulk-sub">{names}</div>
          </div>
          {!running && !finished && (
            <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
          )}
        </div>

        <div className="modal-body bulk-body">
          {!finished ? (
            <>
              <div className="bulk-hero">
                <span className="bulk-hero-num">{targets.total}</span>
                <span className="bulk-hero-label">
                  task{targets.total === 1 ? "" : "s"} currently shown
                </span>
              </div>

              <div className="bulk-stats">
                <div className="bulk-stat">
                  <span className="bulk-stat-dot ok" />
                  <span className="bulk-stat-label">Kept for other assignees</span>
                  <span className="bulk-stat-num">{targets.willUpdate.length}</span>
                </div>
                <div className={`bulk-stat${targets.willDelete.length ? " danger" : ""}`}>
                  <span className={`bulk-stat-dot${targets.willDelete.length ? " bad" : ""}`} />
                  <span className="bulk-stat-label">Left with nobody → deleted</span>
                  <span className="bulk-stat-num">{targets.willDelete.length}</span>
                </div>
              </div>

              {targets.willDelete.length > 0 && (
                <div className="bulk-warn">
                  <span>⚠</span>
                  <span>
                    {targets.willDelete.length} task{targets.willDelete.length === 1 ? "" : "s"} will be
                    permanently deleted — {targets.willDelete.length === 1 ? "it has" : "they have"} no
                    other assignee. This cannot be undone.
                  </span>
                </div>
              )}

              {running ? (
                <>
                  <div className="bulk-progress-track">
                    <div className="bulk-progress-fill" style={{ width: `${planned ? (done / planned) * 100 : 0}%` }} />
                  </div>
                  <div className="bulk-progress-text">
                    <span>{done} of {planned} processed</span>
                    <span>{planned ? Math.round((done / planned) * 100) : 0}%</span>
                  </div>
                  <div className="bulk-hint">Keep this tab open until it finishes.</div>
                </>
              ) : (
                <>
                  <label className="bulk-confirm-label" htmlFor="bulk-confirm">
                    Type <b>{targets.total}</b> to confirm
                  </label>
                  <input
                    id="bulk-confirm"
                    className="bulk-confirm-input"
                    value={confirmText}
                    onChange={e => setConfirmText(e.target.value)}
                    placeholder={String(targets.total)}
                    autoComplete="off"
                    autoFocus
                  />
                </>
              )}
            </>
          ) : (
            <>
              <div className="bulk-done">
                <span className="bulk-done-icon">{failed.length ? "⚠️" : "✅"}</span>
                <div>
                  <div className="bulk-done-title">
                    {failed.length ? "Finished with some errors" : "All done"}
                  </div>
                  <div className="bulk-done-sub">
                    {succeeded} of {planned} task{planned === 1 ? "" : "s"} updated
                    {failed.length > 0 && ` · ${failed.length} failed`}
                  </div>
                </div>
              </div>
              {failed.length > 0 && (
                <div className="bulk-fail">
                  <b>Safe to run again</b> — re-open this and repeat; only the tasks that
                  still list {names} will be touched.
                  <div style={{ marginTop: 8 }}>{failed.length} failed and were left unchanged:</div>
                  <ul style={{ margin: "6px 0 0 16px", padding: 0 }}>
                    {failed.slice(0, 20).map((f, i) => <li key={i}>{f.label} — {f.msg}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {finished ? (
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          ) : running ? (
            <button className="btn" onClick={() => { cancelRef.current = true; }}>Stop</button>
          ) : (
            <>
              <button className="btn" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-danger"
                disabled={!canRun}
                style={{ opacity: canRun ? 1 : 0.5, cursor: canRun ? "pointer" : "not-allowed" }}
                onClick={run}
              >
                Remove from {targets.total} task{targets.total === 1 ? "" : "s"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
